import React, { useState, useEffect, useMemo, useRef, createContext, useContext } from "react";
import { AppProvider, useAppContext } from "@/AppContext";
import "./../index.less";
import ResultJSON from "./resultJSON";
import materialsDB from "@/constants/materialsDB";
import { rebuildFormula } from "@/config/func";
import CalculateTask3DYCZPage from "./calculateTask3DYCZPage";
import CalculateTask3LGPage from "./calculateTask3LG";
import userInfo from "@/Components/user";
import { getJob, getSubJob, subjaiReportJobs, aiReportJobStatus, aiReportDownload } from "@/services/common";
import Params from "../TaskParams/taskIndex";
import { Spin, Button, message, Modal, Checkbox, Input } from "antd";
import { fetchAllTrayHydraulics } from "@/utils/exportReport";
import { ExportOutlined, FundOutlined, DownloadOutlined, RedoOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

// 关键组分上下文：供子组件共享选中的关键组分
export const KeyComponentsContext = createContext<{
  keyComponents: string[];
  setKeyComponents: React.Dispatch<React.SetStateAction<string[]>>;
  keyComponentsEverSet: React.MutableRefObject<boolean>;
}>({
  keyComponents: [],
  setKeyComponents: () => {},
  keyComponentsEverSet: { current: false },
});

export const useKeyComponents = () => useContext(KeyComponentsContext);

// 对比数据上下文：单元操作/流股/输入参数三处页面统一共享
export const CompareDataContext = createContext<{
  compareItems: any[];
  setCompareItems: (items: any[]) => void;
  timeRangeItems: any[];
  setTimeRangeItems: React.Dispatch<React.SetStateAction<any[]>>;
}>({
  compareItems: [],
  setCompareItems: () => {},
  timeRangeItems: [],
  setTimeRangeItems: () => {},
});

export const useCompareData = () => useContext(CompareDataContext);

// ---- 简易 markdown 渲染（用于展示 AI 报告返回的 analysis 分析内容） ----
const analysisInline = (text: string): React.ReactNode => {
  const segs = String(text).split(/(\*\*[^*]+\*\*|`[^`]*`)/g).filter(Boolean);
  return segs.map((seg, i) => {
    if (seg.startsWith("**") && seg.endsWith("**")) {
      return <strong key={i}>{seg.slice(2, -2)}</strong>;
    }
    if (seg.startsWith("`") && seg.endsWith("`")) {
      return (
        <code key={i} style={{ background: "#f0f0f0", padding: "0 4px", borderRadius: 3 }}>
          {seg.slice(1, -1)}
        </code>
      );
    }
    return <React.Fragment key={i}>{seg}</React.Fragment>;
  });
};

// ---- markdown 表格解析（结论里可能带表格） ----
//按 | 切分一行，去掉首尾竖线
const splitTableRow = (line: string): string[] =>
  String(line).trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((cell) => cell.trim());
//是否为表格行（以 | 开头结尾）
const isTableRow = (line: string): boolean => /^\s*\|.*\|\s*$/.test(String(line));
//是否为分隔行，如 | :--- | ---: | :-: |
const isTableSeparator = (line: string): boolean => {
  const cells = splitTableRow(line);
  return cells.length > 0 && cells.every((cell) => /^:?-{2,}:?$/.test(cell));
};
//表格对齐方式由分隔行决定
const tableCellAlign = (separator?: string): "left" | "center" | "right" => {
  if (!separator) return "left";
  if (separator.startsWith(":") && separator.endsWith(":")) return "center";
  if (separator.endsWith(":")) return "right";
  return "left";
};
//引擎返回的表格常被压成一行（行与行之间只剩 " | | "），先还原成多行
const expandFlattenedTables = (lines: string[]): string[] => {
  const result: string[] = [];
  lines.forEach((line) => {
    //仅处理"含分隔行"的表格行，正常多行表格的表头/分隔行不会被改写
    if (!isTableRow(line) || !/\|\s*:?-{2,}:?\s*\|/.test(line)) {
      result.push(line);
      return;
    }
    result.push(...line.replace(/\|\s*\|/g, "|\n|").split("\n"));
  });
  return result;
};

const renderAnalysisMarkdown = (md: string): React.ReactNode => {
  if (!md) return null;
  const lines = expandFlattenedTables(String(md).replace(/\r\n/g, "\n").split("\n"));
  const nodes: React.ReactNode[] = [];
  const headingTag: Record<string, string> = { 1: "h1", 2: "h2", 3: "h3", 4: "h4", 5: "h5", 6: "h6" };
  let paragraph: string[] = [];
  let listItems: { text: string; ordered: boolean }[] = [];
  const pushKey = () => nodes.length;
  const flushParagraph = () => {
    if (paragraph.length > 0) {
      const text = paragraph.join(" ");
      nodes.push(
        <p key={pushKey()} style={{ margin: "6px 0", lineHeight: 1.9 }}>
          {analysisInline(text)}
        </p>,
      );
      paragraph = [];
    }
  };
  const flushList = () => {
    const first = listItems[0];
    if (!first) return;
    const ListTag: any = first.ordered ? "ol" : "ul";
    nodes.push(
      <ListTag key={pushKey()} style={{ paddingLeft: 24, margin: "6px 0", lineHeight: 1.9 }}>
        {listItems.map((li, i) => (
          <li key={i}>{analysisInline(li.text)}</li>
        ))}
      </ListTag>,
    );
    listItems = [];
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trimEnd();
    //表格：表头行 + 分隔行 + 若干数据行
    if (isTableRow(line) && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      flushParagraph();
      flushList();
      const headerCells = splitTableRow(line);
      const alignCells = splitTableRow(lines[i + 1]);
      const rows: string[][] = [];
      let j = i + 2;
      while (j < lines.length && isTableRow(lines[j])) {
        rows.push(splitTableRow(lines[j]));
        j++;
      }
      const thStyle = (ci: number): React.CSSProperties => ({
        border: "1px solid #d9d9d9",
        background: "#f2f4f8",
        padding: "6px 10px",
        textAlign: tableCellAlign(alignCells[ci]),
        whiteSpace: "nowrap",
        fontWeight: 600,
      });
      const tdStyle = (ci: number): React.CSSProperties => ({
        border: "1px solid #d9d9d9",
        padding: "6px 10px",
        textAlign: tableCellAlign(alignCells[ci]),
        verticalAlign: "top",
      });
      nodes.push(
        <div key={pushKey()} style={{ margin: "10px 0", overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 15 }}>
            <thead>
              <tr>
                {headerCells.map((cell, ci) => (
                  <th key={ci} style={thStyle(ci)}>{analysisInline(cell)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((cells, ri) => (
                <tr key={ri}>
                  {headerCells.map((_, ci) => (
                    <td key={ci} style={tdStyle(ci)}>{analysisInline(cells[ci] ?? "")}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      i = j - 1;
      continue;
    }
    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      flushParagraph();
      flushList();
      const level = heading[1].length;
      nodes.push(
        React.createElement(
          headingTag[level],
          { key: pushKey(), style: { margin: "12px 0 6px" } },
          analysisInline(heading[2]),
        ),
      );
      continue;
    }
    const bullet = line.match(/^\s*[-*]\s+(.+)$/);
    const numbered = line.match(/^\s*\d+[.)]\s+(.+)$/);
    if (bullet || numbered) {
      flushParagraph();
      const ordered = !!numbered;
      if (listItems.length > 0 && listItems[0].ordered !== ordered) flushList();
      listItems.push({ text: (bullet || numbered)![1], ordered });
      continue;
    }
    flushList();
    if (line.trim() === "") {
      flushParagraph();
      continue;
    }
    paragraph.push(line);
  }
  flushParagraph();
  flushList();
  return nodes;
};

const Component = (props: any) => {
  const { subJobid, item_name, job_id, type ,refreshResVariable,DataSource} = props;
  console.log("DataSource", DataSource);
  
  // 从链路数据中提取流股描述映射
  const streamDescMap = useMemo(() => {
    if (!DataSource?.data?.linkDataArray) return {};
    const map: Record<string, string> = {};
    DataSource.data.linkDataArray.forEach((link: any) => {
      if (link.linkName && link.description) {
        map[link.linkName] = link.description;
      }
    });
    return map;
  }, [DataSource]);

  // 从节点数据中提取单元描述映射
  const elementDescMap = useMemo(() => {
    if (!DataSource?.data?.nodeDataArray) return {};
    const map: Record<string, string> = {};
    DataSource.data.nodeDataArray.forEach((node: any) => {
      if ((node.text || node.nodeName) && node.description) {
        map[node.text || node.nodeName] = node.description;
      }
    });
    return map;
  }, [DataSource]);

  // 从节点数据中提取流股→元件映射（源元件、目标元件）
  const streamNodeMap = useMemo(() => {
    if (!DataSource?.data?.linkDataArray || !DataSource?.data?.nodeDataArray) return {};
    const nodeKeyMap: Record<number, string> = {};
    DataSource.data.nodeDataArray.forEach((node: any) => {
      if (node.key !== undefined) {
        nodeKeyMap[node.key] = node.nodeName || node.text || "";
      }
    });
    const map: Record<string, { fromName: string; toName: string }> = {};
    DataSource.data.linkDataArray.forEach((link: any) => {
      if (link.linkName) {
        map[link.linkName] = {
          fromName: nodeKeyMap[link.from] || "",
          toName: nodeKeyMap[link.to] || "",
        };
      }
    });
    return map;
  }, [DataSource]);
  const { state, actions } = useAppContext();

  const [activeTab, setActiveTab] = useState("");
  const [tempUnit, setTempUnit] = useState("℃");
  const [pressUnit, setPressUnit] = useState("MPa");
  const [transInput, setTransInput] = useState({});
  const [input, setInput] = useState<any>({});
  const [response, setResponse] = useState<any>({});
  const [error, setError] = useState<any>({});
// 收敛检查状态：用于在检查结果是否收敛时显示加载中
  const [convergenceChecked, setConvergenceChecked] = useState(false);
  // 检查结果是否收敛：只要有一个元素的 IsConverged === 0 就算不收敛
  const isConverged = useMemo(() => {
    if (!response?.elements?.length) return true;
    return !response.elements.some((item: any) => item?.result?.IsConverged === 0);
  }, [response]);
  // console.log("isConverged", isConverged);
  // console.log("convergenceChecked", convergenceChecked);

  const [showContent, setShowContent] = useState(isConverged);
  // console.log("showContent", showContent);

  const [refreshKey, setRefreshKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [hydraulicsModalOpen, setHydraulicsModalOpen] = useState(false); // 是否选择导出水力学部分
  const [includeHydraulics, setIncludeHydraulics] = useState(false); // 默认不包含水力学负荷图
  // 导出对比报告前的优化指标填写弹窗
  const [compareReqModalOpen, setCompareReqModalOpen] = useState(false);
  const [compareRequirement, setCompareRequirement] = useState(
    "评价指标：尾气洗涤部分的甲醇质量浓度不超过50mg/Nm³",
  );
  // 报告生成完成后返回的分析内容（markdown），有值时在页面上以"对比分析"页签展示
  const [analysisText, setAnalysisText] = useState<string>("");
  // 报告生成完成后返回的 PDF 地址（file_url），在"对比分析"页签底部提供下载
  const [reportFileUrl, setReportFileUrl] = useState<string>("");
  // 当前页面展示的分析报告任务 id（用于 aiReportDownload 下载 PDF）
  const [reportPdfJobId, setReportPdfJobId] = useState<string>("");
  const [pdfDownloading, setPdfDownloading] = useState(false);
  // 对比分析报告已生成（按钮从"生成分析建议"切换为"导出分析报告"）
  const [compareReportReady, setCompareReportReady] = useState(false);
  // 记录上一次 analysis，仅在其从无到有时自动跳转一次，避免抢占用户手动切换的页签
  const prevAnalysisText = useRef("");
  // 分析来源：compare → "对比分析"页签；calc → "优化建议"页签
  const [analysisSource, setAnalysisSource] = useState<"" | "calc" | "compare">("");
  const [keyComponents, setKeyComponents] = useState<string[]>([]);
  const keyComponentsEverSet = useRef(false);
  // 单元操作页当前选中的元件名（供流股页默认选中该元件的输入输出流股）
  const [activeElementName, setActiveElementName] = useState<string>("");

  // 切换任务（查看任务结果 / 子任务单元操作结果）时重置关键组分初始化标志和对比项
  useEffect(() => {
    keyComponentsEverSet.current = false;
    setKeyComponents([]);
    setCompareItems([]);
    setTimeRangeItems([]);
    setActiveElementName("");
    setAnalysisText("");
    setReportFileUrl("");
    setReportPdfJobId("");
    setCompareReportReady(false);
    setAnalysisSource("");
    prevAnalysisText.current = "";
  }, [job_id]);

  // 对比数据状态：单元操作/流股/输入参数三处页面统一由父组件管理
  const [compareItems, setCompareItems] = useState<any[]>([]);
  // 手动创建的时间范围项目：与对比项一样三处页面共享
  const [timeRangeItems, setTimeRangeItems] = useState<any[]>([]);
  const [compareExporting, setCompareExporting] = useState(false);

  // 构建 AI 报告所需的 hydraulics 参数（任务id → 塔名 → 塔板数组，画负荷图用）
  const buildHydraulicsPayload = async (): Promise<any> => {
    const hydraulicsPayload: any = {};
    const elements = response?.elements || [];
    const inputElements = input?.inputs?.elements || [];

    // 只处理精馏塔元件，塔板数取仿真结果里的逐板水力学数组长度
    const towers: { name: string; elInput: any; elResult: any; trayCount: number }[] = [];
    elements.forEach((el: any) => {
      const elType = el.type || "";
      if (elType !== "DistilStrict" && elType !== "DistilShortCut") return;
      const elResult = el.result || {};
      const trayCount =
        elResult?.hydraulics?.delta_P_tray?.length || elResult?.L_kg_per_h?.length || 0;
      if (!trayCount) return;
      const elInput = inputElements.find((ie: any) => ie.name === el.name) || {};
      towers.push({ name: el.name, elInput, elResult, trayCount });
    });
    if (towers.length === 0) return hydraulicsPayload;

    hydraulicsPayload[job_id] = {};
    for (const t of towers) {
      try {
        const allTrayData = await fetchAllTrayHydraulics(t.elInput, t.elResult, t.trayCount);
        hydraulicsPayload[job_id][t.name] = allTrayData
          .filter(Boolean)
          .map((d: any) => ({
            L_values: d?.L_values?.["kg/h"] || [],
            V_flood: d?.V_flood?.["kg/h"] || [],
            V_leakage: d?.V_leakage?.["kg/h"] || [],
            V_max_entrain: d?.V_max_entrain?.["kg/h"] || [],
            L_op: d?.L_op?.["kg/h"] ?? 0,
            V_op: d?.V_op?.["kg/h"] ?? 0,
            V_L_ratio_op: d?.V_L_ratio_op?.["kg/h"] ?? 0,
            L_min: d?.L_min?.["kg/h"] ?? 0,
            L_max: d?.L_max?.["kg/h"] ?? 0,
          }));
      } catch {
        // 单座塔水力学计算失败不阻塞整个报告
      }
    }
    return hydraulicsPayload;
  };

  // 导出报告：AI 报告接口，提交任务 → 轮询状态 → 下载 PDF
  const handleExportReport = async () => {
    setExporting(true);
    const msgKey = "ai-report-export";
    try {
      // 子任务 id（单元件计算结果 subJob、组合计算结果 multiJobs）都放 subjob_ids，只有全流程任务(job)放 job_ids；水力学结果按用户选择传
      const isSubJob = type === "subJob" || type === "multiJobs";
      const hydraulicsPayload = includeHydraulics ? await buildHydraulicsPayload() : {};
      const analysisKind = "calc"; // 本次报告类型：calc → 返回的 analysis 以"优化建议"页签展示

      // 1) 提交报告任务
      const submitRes = await subjaiReportJobs(
        {
          report_type: "calc",
          job_ids: isSubJob ? [] : [job_id],
          subjob_ids: isSubJob ? [job_id] : [],
          hydraulics: hydraulicsPayload,
          chart_components: keyComponents.join(",") || undefined,
          with_analysis: true,
          with_charts: true,
          name: input?.name || "计算报告",
          flow_id: null,
          user_id: userInfo.id,
        },
        userInfo.access_token,
      );
      const reportJobId = submitRes?.data?.job_id;
      if (!reportJobId) {
        throw new Error(submitRes?.data?.message || "提交报告任务失败");
      }

      // 2) 轮询任务状态（建议 4 秒一次）
      message.success({ content: "报告正在生成中，请稍后在右上角导出列表中查看", key: msgKey, duration: 3 });
      // 通知顶部导出列表图标播放"加入购物车"动画
      window.dispatchEvent(new CustomEvent("ai-report-submitted"));
      let jobData: any = null;
      for (;;) {
        await new Promise((resolve) => setTimeout(resolve, 4000));
        const statusRes = await aiReportJobStatus(reportJobId, userInfo.access_token);
        jobData = statusRes?.data;
        if (!jobData || jobData.status === "done") {
          // 报告完成：保存分析内容（有 analysis 时新增分析页签）与 PDF 下载信息，并通知顶部导出列表刷新任务状态
          setAnalysisSource(analysisKind);
          setAnalysisText(jobData?.result?.analysis || "");
          setReportFileUrl(jobData?.file_url || jobData?.result?.file_url || "");
          setReportPdfJobId(String(reportJobId || jobData?.job_id || ""));
          window.dispatchEvent(new CustomEvent("ai-report-refresh"));
          break;
        }
        if (jobData.status === "error") {
          throw new Error(jobData?.result?.error_message || "报告生成失败");
        }
      }

      // 3) 下载 PDF
      message.success({ content: "报告生成完成，正在下载...", key: msgKey, duration: 2 });
      const dlRes = await aiReportDownload(reportJobId, userInfo.access_token);
      const blob = new Blob([dlRes.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = jobData?.result?.filename || `ai-report-${reportJobId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      message.success({ content: "报告导出成功", key: msgKey, duration: 2 });
    } catch (err: any) {
      message.error({ content: err?.message || "报告导出失败", key: msgKey, duration: 5 });
    } finally {
      setExporting(false);
    }
  };

  // 组装对比报告请求体：本任务放最前边，子任务 id（含组合计算）放 subjob_ids，其余任务 id 放 job_ids，设计值/运行数据放 more_data
  const buildComparePayload = (): any => {
    const jobIds: number[] = [];
    const subjobIds: number[] = [];
    const moreData: Record<string, any> = {};

    // 被对比的本任务放到对应数组最前边（组合计算结果 multiJobs 也是子任务，同样放 subjob_ids）
    const currentId = Number(job_id);
    if (type === "subJob" || type === "multiJobs") {
      subjobIds.push(currentId);
    } else {
      jobIds.push(currentId);
    }

    compareItems.forEach((item: any) => {
      const itemId = Number(item.id);
      if (item.type === "otherCalc") {
        // 对比的子任务（其他计算结果）
        if (!subjobIds.includes(itemId)) subjobIds.push(itemId);
        return;
      }
      if (item.type === "task") {
        // 对比的仿真任务
        if (!jobIds.includes(itemId)) jobIds.push(itemId);
        return;
      }
      // 设计值 / 运行数据 → more_data（对比报告没有水力学参数）
      let label = item.values?.name;
      if (!label) {
        if (item.type === "design") label = `设计值(${item.id})`;
        else if (item.type === "actual") label = `运行数据(${item.id})`;
        else if (item.date) label = `运行数据 ${item.date}`;
        else label = String(item.id);
      }
      moreData[label] = {
        // 运行数据优先用接口返回的原始 input/output，其次用完整换算数据，最后兜底 mix_input
        input: item.values?.rawInput || item.values?.fullInput || item.values?.mix_input || {},
        output: item.values?.rawOutput || item.values?.fullOutput || item.values?.mix_input || {},
      };
    });

    return {
      job_ids: jobIds,
      subjob_ids: subjobIds,
      more_data: moreData,
    };
  };

  // 打开导出对比报告的优化指标填写弹窗（需先有对比项）
  const openCompareReqModal = () => {
    if (compareItems.length === 0) {
      message.warning("请先在【对比设计值/运行数据】选择对比项后再导出对比报告");
      return;
    }
    setCompareReqModalOpen(true);
  };

  // 通过 aiReportDownload 接口下载当前分析报告对应的 PDF
  const handleAnalysisPdfDownload = async () => {
    if (!reportPdfJobId) {
      if (reportFileUrl) window.open(reportFileUrl, "_blank");
      else message.warning("暂无可下载的报告");
      return;
    }
    setPdfDownloading(true);
    const pdfKey = "analysis-pdf-download";
    try {
      message.loading({ content: "正在下载 PDF，请稍候...", key: pdfKey, duration: 0 });
      const dlRes = await aiReportDownload(reportPdfJobId, userInfo.access_token);
      const blob = new Blob([dlRes.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = reportFileUrl.split("/").pop() || `ai-report-${reportPdfJobId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      message.success({ content: "PDF 下载完成", key: pdfKey, duration: 2 });
    } catch (err: any) {
      message.error({ content: err?.message || "PDF 下载失败", key: pdfKey, duration: 3 });
    } finally {
      setPdfDownloading(false);
    }
  };

  // 导出对比报告：AI 报告接口，提交任务 → 轮询状态 → 下载 PDF（没有 hydraulics）
  const handleCompareExportConfirm = async () => {
    setCompareReportReady(false);
    setCompareExporting(true);
    const msgKey = "ai-report-compare-export";
    try {
      if (compareItems.length === 0) {
        throw new Error("请先在【对比设计值/运行数据】选择对比项后再导出对比报告");
      }

      // 1) 提交报告任务（优化指标 requirement 仅对比报告携带，非空才传）
      const requirement = compareRequirement.trim();
      const analysisKind = "compare"; // 本次报告类型：compare → 返回的 analysis 以"对比分析"页签展示
      const submitRes = await subjaiReportJobs(
        {
          report_type: "compare",
          ...buildComparePayload(),
          chart_components: keyComponents.join(",") || undefined,
          ...(requirement ? { requirement } : {}),
          with_analysis: true,
          with_charts: true,
          name: input?.name ? `${input.name}对比报告` : "对比分析报告",
          flow_id: null,
          user_id: userInfo.id,
        },
        userInfo.access_token,
      );
      console.log("导出对比报告提交参数:", submitRes);
      const reportJobId = submitRes?.data?.job_id;
      if (!reportJobId) {
        throw new Error(submitRes?.data?.message || "提交报告任务失败");
      }

      // 2) 轮询任务状态（建议 4 秒一次）
      message.success({ content: "报告正在生成中，请稍后在右上角导出列表中查看", key: msgKey, duration: 3 });
      // 通知顶部导出列表图标播放"加入购物车"动画
      window.dispatchEvent(new CustomEvent("ai-report-submitted"));
      let jobData: any = null;
      for (;;) {
        await new Promise((resolve) => setTimeout(resolve, 4000));
        const statusRes = await aiReportJobStatus(reportJobId, userInfo.access_token);
        jobData = statusRes?.data;
        if (!jobData || jobData.status === "done") {
          // 报告完成：保存分析内容（有 analysis 时新增分析页签）与 PDF 下载信息，并通知顶部导出列表刷新任务状态
          setAnalysisSource(analysisKind);
          setAnalysisText(jobData?.result?.analysis || "");
          setReportFileUrl(jobData?.file_url || jobData?.result?.file_url || "");
          setReportPdfJobId(String(reportJobId || jobData?.job_id || ""));
          window.dispatchEvent(new CustomEvent("ai-report-refresh"));
          break;
        }
        if (jobData.status === "error") {
          throw new Error(jobData?.result?.error_message || "报告生成失败");
        }
      }

      // 3) 对比分析报告不直接下载：把顶部按钮切换为"导出分析报告"，点击后走 aiReportDownload 下载
      setCompareReportReady(true);
      message.success({
        content: "分析建议已生成，点击「导出分析报告」下载 PDF",
        key: msgKey,
        duration: 3,
      });
    } catch (err: any) {
      message.error({ content: err?.message || "对比报告导出失败", key: msgKey, duration: 5 });
    } finally {
      setCompareExporting(false);
    }
  };

  useEffect(() => 
  {
    if(refreshResVariable>0)
    {
      setRefreshKey(k => k + 1)
    }
  }, [refreshResVariable]);

  useEffect(() => {
    if (response && Object.keys(response).length > 0 && typeof response !== "string") {
      setConvergenceChecked(false);
      const timer = setTimeout(() => setConvergenceChecked(true), 300);
      return () => clearTimeout(timer);
    } else {
      setConvergenceChecked(false);
    }
  }, [response]);

  // 当 response 变化导致收敛状态改变时，重置 showContent
  useEffect(() => {
    setShowContent(isConverged);
  }, [isConverged]);

  // 切换任务时重置 showContent（处理两次都不收敛的边界情况）
  useEffect(() => {
    setShowContent(isConverged);
  }, [response]);

  // 报告返回 analysis（从无到有）时自动跳到"对比分析"页签；之后不再抢占用户手动切换的页签；
  // 内容被清空且停在改页签时退回默认页签
  useEffect(() => {
    if (analysisText && !prevAnalysisText.current) {
      setActiveTab("analysis");
    } else if (!analysisText && activeTab === "analysis") {
      setActiveTab("element");
    }
    prevAnalysisText.current = analysisText;
  }, [analysisText, activeTab]);
  // 分析页签名称：calc 报告 → 优化建议；compare 报告 → 对比分析
  const analysisLabel = analysisSource === "calc" ? "优化建议" : "对比分析";
  const tabs = [
    { key: "element", label: "单元操作" },
    { key: "stream", label: "流股" },
    // { key: "heat", label: "热交换" },
    // { key: "economy", label: "经济效益" },
    { key: "json", label: "完整输入输出" },
    { key: "params", label: "输入参数" },
    // 报告生成完成后返回了 analysis 时，按报告类型增加"优化建议"/"对比分析"页签
    ...(analysisText ? [{ key: "analysis", label: analysisLabel }] : []),
  ];
  const unstartedTabs = [
    { key: "error", label: "信息面板" },
    { key: "params", label: "输入参数" },
  ];

  useEffect(() => {
    if (type === "job") {
      getJob(job_id, userInfo.access_token).then((r) => {
        console.log("标题拼接", r);
        if (r.status === 200) {
          setInput({
            //全流程任务名称后附创建时间，与单元件/组合计算结果的标题展示保持一致
            name: r.data.updated_at
              ? `${r.data.name}：${dayjs(r.data.updated_at).add(8, "hours").format("YYYY-MM-DD HH:mm:ss")}`
              : r.data.name,
            flow_id: r.data.flow_id,
            description: r.data.description,
            inputs: JSON.parse(r.data.input),
          });
          if(r.data.status==="unstart"){
            setResponse("unstart");
            setActiveTab("error");
            return;
          }
          if(r.data.status==="pending"){
            setResponse("pending");
            setActiveTab("error");
            return;
          }
          if(r.data.status==="error"){
            setResponse("error");
            setActiveTab("error");
            setError(JSON.parse(r.data.output))
            // console.log('error', JSON.parse(r.data.output));
            
            return;
          }
          // console.log('tempInput',  JSON.parse(r.data.input));
          console.log('r.data.output', r.data);
          setResponse(JSON.parse(r.data.output.replace(/NaN/g, "0")));
          setActiveTab("element");
          // setResponse(response0423.response);
        }
      });
    }else if(type==='subJob'){
      getSubJob(job_id, userInfo.access_token).then((r) => {
        console.log("rsubJob:", r);
        if (r.status === 200) {
          setInput({
            name: `${r.data.name}：${dayjs(r.data.created_at).add(8,'hours').format("YYYY-MM-DD HH:mm:ss")}`,
            // flow_id: r.data.flow_id,
            // description: r.data.description,
            inputs:r.data.input,
          });
          if(r.data.status==="unstart"){
            setResponse("unstart");
            setActiveTab("error");
            return;
          }
          if(r.data.status==="pending"){
            setResponse("pending");
            setActiveTab("error");
            return;
          }
          if(r.data.status==="error"){
            setResponse("error");
            setActiveTab("error");
            setError(r.data.output ?? {});
            return;
          }
          // console.log('tempInput',  JSON.parse(r.data.input));
          setResponse(r.data.output);
          setActiveTab("element");
          // console.log('r.data.output', JSON.parse(r.data.output.replace(/NaN/g, '0')));
          // setResponse(response0423.response);
        }
      });
    }else if(type==='multiJobs'){
      getSubJob(job_id, userInfo.access_token).then((r) => {
        if (r.status === 200) {
          console.log("组合计算结果",r.data);
          setInput({
            name: `${r.data.name}：${dayjs(r.data.created_at).add(8,'hours').format("YYYY-MM-DD HH:mm:ss")}`,
            // flow_id: r.data.flow_id,
            // description: r.data.description,
            inputs:r.data.input,
          });
          if(r.data.status==="unstart"){
            setResponse("unstart");
            setActiveTab("error");
            return;
          }
          if(r.data.status==="pending"){
            setResponse("pending");
            setActiveTab("error");
            return;
          }
          if(r.data.status==="error"){
            setResponse("error");
            setActiveTab("error");
            setError(r.data.output ?? {});
            return;
          }
          // console.log('tempInput',  JSON.parse(r.data.input));
          setResponse(r.data.output);
          setActiveTab("element");
          // console.log('r.data.output', JSON.parse(r.data.output.replace(/NaN/g, '0')));
          // setResponse(response0423.response);
        }
      });
    }
  }, [job_id, type, refreshKey]);

  const transformMaterials = (rawMaterials: any[]) => {
    return rawMaterials.map((material, index) => {
      const m: any = materialsDB.find((item: any) => item.cas === material.cas);
      return {
        cas: material.cas,
        Molecular_formula: rebuildFormula(m.Molecular_formula),
        Name_ZH: m.Name_ZH,
        MW: m.MW,
        index: index,
        molar_frac: material.molar_frac,
      };
    });
  };
  useEffect(() => {
    // 安全检查：确保 input 和 inputs 存在
    if (!input || !input.inputs || !input.inputs.streams) {
      console.log("input 或 inputs.streams 不存在");
      return;
    }

    // 找到第一个有 materials 的流股
    const streamWithMaterials = input.inputs.streams.find(
      (item: any) => item && item.materials && item.materials.length > 0,
    );

    if (!streamWithMaterials) {
      console.log("没有找到包含 materials 的流股");
      return;
    }

    const transformedMaterials = {
      materials: transformMaterials(streamWithMaterials.materials),
    };

    setTransInput(transformedMaterials);
  }, [input]);
  console.log("response",response);
  return (
    <CompareDataContext.Provider value={{ compareItems, setCompareItems, timeRangeItems, setTimeRangeItems }}>
    <KeyComponentsContext.Provider value={{ keyComponents, setKeyComponents, keyComponentsEverSet }}>
    <>
    {state.curFlow &&state.curProject && state.projectList && input &&
      // {input &&
      Object.keys(input).length > 0 &&
      response &&
      Object.keys(response).length > 0 ? (
        <div className="mainContent" style={{ height: "calc(100vh - 65px)" }}>
          <div className="mainTask" style={{ width: "100%" }}>
            <div className="tabContainer" style={{ position: "relative" }}>
              {/* 标题过长（名称已带创建时间）时截断为省略号，悬停用原生 title 显示完整文字；
                  右侧有"重新生成分析建议"按钮时宽度收紧，避免挤压按钮 */}
              <div
                className="tabTitle"
                title={input?.name}
                style={{
                  maxWidth: typeof response !== "string" && compareReportReady ? 240 : 420,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {input?.name}
              </div>

              {/* Tab 按钮组 */}
              <div className="tabButtonGroup">
                {((response==="unstart" || response==="error" || response==="pending") ? unstartedTabs : tabs).map((tab) => (
                  <div
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`tabItem ${activeTab === tab.key ? "tabActive" : "tabInactive"}`}
                  >
                    <span
                      className={`tabText ${activeTab === tab.key ? "tabTextActive" : "tabTextInactive"}`}
                    >
                      {tab.label}
                    </span>
                  </div>
                ))}
              </div>

              {/* 导出按钮 - 仅在计算成功时显示 */}
              {typeof response !== "string" && (
                <>
                  <div style={{ position: "absolute", right: 23, top: "20%" }}>
                    <Button style={{}} loading={exporting} onClick={() => setHydraulicsModalOpen(true)}>
                    <ExportOutlined />导出计算报告
                  </Button>
                  {/* 已生成分析建议时，左侧提供"重新生成分析建议"入口：重新走一遍生成分析建议的流程 */}
                  {compareReportReady && (
                    <Button
                      style={{ marginLeft: 10 }}
                      loading={compareExporting}
                      disabled={pdfDownloading}
                      onClick={openCompareReqModal}
                    >
                      <RedoOutlined style={{ marginRight: 6 }} />
                      重新生成分析建议
                    </Button>
                  )}
                  <Button
                    style={
                      compareReportReady
                        ? { marginLeft: 10, background: "#47c10c", borderColor: "#47c10c", color: "#fff", fontWeight: 500,top:"-2px" }
                        : { marginLeft: 10,top:"-2px" }
                    }
                    loading={compareExporting || pdfDownloading}
                    onClick={compareReportReady ? handleAnalysisPdfDownload : openCompareReqModal}
                  >
                    {compareReportReady ? (
                      <>
                        <DownloadOutlined style={{ marginRight: 6 }} />
                        导出分析报告
                      </>
                    ) : (
                      <>
                        <FundOutlined style={{ marginRight: 6 }} />
                        生成分析建议
                      </>
                    )}
                  </Button>
                  </div>

                  {/* 导出报告前选择是否包含水力学部分 */}
                  <Modal
                    title="导出报告"
                    open={hydraulicsModalOpen}
                    onCancel={() => setHydraulicsModalOpen(false)}
                    onOk={() => {
                      setHydraulicsModalOpen(false);
                      handleExportReport();
                    }}
                    okText="开始导出"
                    cancelText="取消"
                    confirmLoading={exporting}
                  >
                    <Checkbox
                      checked={includeHydraulics}
                      onChange={(e) => setIncludeHydraulics(e.target.checked)}
                    >
                      导出水力学计算结果
                    </Checkbox>
                    <div style={{ marginTop: 8, fontSize: 13, color: "rgba(0,0,0,0.45)" }}>
                      不勾选则跳过报告中的水力学部分，仅导出计算结果。
                    </div>
                  </Modal>

                  {/* 导出对比报告前填写优化指标 */}
                  <Modal
                    title="生成分析建议"
                    open={compareReqModalOpen}
                    onCancel={() => setCompareReqModalOpen(false)}
                    onOk={() => {
                      setCompareReqModalOpen(false);
                      handleCompareExportConfirm();
                    }}
                    okText="开始生成"
                    cancelText="取消"
                    confirmLoading={compareExporting}
                  >
                    <div style={{ marginBottom: 8, fontSize: 14, color: "rgba(0,0,0,0.65)" }}>
                      请填写优化指标：
                    </div>
                    <Input.TextArea
                      rows={12}
                      value={compareRequirement}
                      onChange={(e) => setCompareRequirement(e.target.value)}
                      placeholder="例如：评价指标：甲醇质量浓度 ≤50mg/m³；再沸器热负荷越低越好；塔顶采出纯度 ≥99.5%"
                    />
                    {/* <div style={{ marginTop: 8, fontSize: 12, color: "rgba(0,0,0,0.35)" }}>
                      该优化指标将随对比报告一起提交；不填写则报告不带优化指标。
                    </div> */}
                  </Modal>
                </>
              )}
            </div>

            {/* Tab 内容区域 */}
            <div className="tabContent">
              {!input ? (
                <div className="loadingText">加载中...或未找到对应任务数据</div>
              ) : activeTab === "params" ? (
                <Params job_id={job_id} type={type} subJobid={subJobid} item_name={item_name} />
              ) : response === "error" ? (
                <div className="sectionContent error-state-container">
                  <div className="error-content">
                    <div className="error-icon">❌</div>
                    <h4 className="sectionTitle error-title">计算失败</h4>
                    <div className="error-info-box">
                      <p className="error-type">
                        错误类型：{error?.error_type || "未知错误"}
                      </p>
                    </div>
                  </div>
                </div>
              ) : response === "unstart" ? (
                <div className="sectionContent error-state-container">
                  <div className="unstarted-content">
                    <div className="unstarted-icon">⏸️</div>
                    <h4 className="sectionTitle unstarted-title">任务未进行计算</h4>
                    <p className="unstarted-tip">请先启动任务计算以查看结果</p>
                  </div>
                </div>
              ) : response === "pending" ? (
                <div className="sectionContent error-state-container">
                  <div className="calculating-content">
                    <div className="calculating-icon">⏳</div>
                    <h4 className="sectionTitle calculating-title">正在计算中，请稍候查看...</h4>
                    <p className="calculating-tip">请稍候，计算正在进行中</p>
                    <Button onClick={() => setRefreshKey(k => k + 1)}>
                      刷新
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {/* 正在检查结果是否收敛 */}
                  {!convergenceChecked ? (
                    <div className="convergence-checking">
                      <Spin size="large" />
                      <span className="convergence-checking-text">正在检查结果是否收敛...</span>
                    </div>
                  ) : (
                    <>
                      {/* 不收敛提示：仅对 element 和 stream 生效 */}
                      {!isConverged && !showContent && (activeTab === "element" || activeTab === "stream") ? (
                    <div className="converged-warning">
                      <div className="converged-warning-content">
                        <span className="converged-warning-icon">⚠</span>
                        <span>此计算结果不收敛，是否继续查看不收敛的结果？</span>
                        <button
                          className="converged-warning-btn"
                          onClick={() => setShowContent(true)}
                        >
                          继续查看
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {activeTab === "element" && (
                        <CalculateTask3DYCZPage
                          input={input}
                          transInput={transInput}
                          results={response}
                          subJobid={subJobid}
                          item_name={item_name}
                          job_id={job_id}
                          type={type}
                          streamDescMap={streamDescMap}
                          streamNodeMap={streamNodeMap}
                          elementDescMap={elementDescMap}
                          tempUnit={tempUnit}
                          pressUnit={pressUnit}
                          setTempUnit={setTempUnit}
                          setPressUnit={setPressUnit}
                          onElementChange={setActiveElementName}
                          activeElementName={activeElementName}
                          compareItems={compareItems}     //元件差异高亮备注
                        />
                      )}
                      {activeTab === "stream" && (
                        <div className="sectionContent">
                          <CalculateTask3LGPage input={input} results={response} job_id={job_id} subJobid={subJobid} type={type} item_name={item_name} streamDescMap={streamDescMap} streamNodeMap={streamNodeMap} elementDescMap={elementDescMap} tempUnit={tempUnit} pressUnit={pressUnit} setTempUnit={setTempUnit} setPressUnit={setPressUnit} activeElementName={activeElementName} />
                        </div>
                      )}
                    </>
                  )}
                    </>
                  )}
                  {activeTab === "heat" && (
                    <div className="sectionContent">
                      <h4 className="sectionTitle">热交换参数</h4>
                    </div>
                  )}
                  {activeTab === "economy" && (
                    <div className="sectionContent">
                      <h4 className="sectionTitle">经济效益参数</h4>
                    </div>
                  )}
                  {activeTab === "json" && <ResultJSON input={input} results={response} />}
                  {activeTab === "params" && <Params job_id={job_id} type={type} subJobid={subJobid} item_name={item_name} />}
                  {/* 分析页签：展示报告生成完成后返回的 analysis 内容（对比分析/优化建议） */}
                  {activeTab === "analysis" && analysisText && (
                    <div
                      className="sectionContent"
                      style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        boxSizing: "border-box",
                        padding: "16px 20px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          minHeight: 0,
                          overflowY: "auto",
                          fontSize: 16,
                          color: "#333",
                          paddingBottom: 48,
                          paddingRight: 8,
                        }}
                      >
                        {renderAnalysisMarkdown(analysisText)}
                      </div>
                    </div>
                  )}
                  {/* {activeTab === "error" && (
                    <div className="sectionContent error-state-container">
                      <div style={{ textAlign: "center", padding: "40px 0" }}>
                        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                        <h4 className="sectionTitle" style={{ color: "#01894c" }}>计算成功</h4>
                        <p style={{ color: "#666", marginTop: 8 }}>未检测到计算错误</p>
                      </div>
                    </div>
                  )} */}
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
    </KeyComponentsContext.Provider>
    </CompareDataContext.Provider>
  );
};

export default Component;
